import React from 'react'
import classnames from 'classnames/bind'
import type { primitives } from '@adc/blueprint'
import { FeatureFlags, useListingContext } from 'common/context/listing'
import {
  Container,
  LeftMarginSection,
} from 'common/modules/bidBanner/container'
import {
  CountdownBefore,
  getServerTimeMoment,
  getStartTimeMoment,
} from 'common/modules/auctionCountdown'
import * as BidDetails from 'common/modules/bidBanner/bidDetails'
import BidForm from 'common/modules/bidBanner/bidForm'
import ProxyBidFormUI from 'common/modules/bidBanner/bidForm/ui/proxy'
import FAQSection from 'common/modules/bidBanner/faqSection'
import Header from 'common/modules/bidBanner/header'
import TimeInformation from 'common/components/bidBanner/timeInformation'
import MatchBidHeader from 'common/components/bidBanner/matchBid/matchBidHeader'
import {
  BID_BANNER_STATES,
  getBidBannerState,
} from 'common/components/bidBanner/states'
import interpolate from '../../../utils/interpolate'
import { formatMoneyWithCentsExceptZero } from '../../../utils/formatters'

const cx = classnames.bind({})

const HEADERS = {
  USER_WITH_PROXY_BID: 'Your proxy bid was submitted.',
  USER_WITH_PROXY_BID_SUBTEXT: `You may place a new proxy bid higher than %s.`,
  USER_WITHOUT_PROXY_BID: 'Place a proxy bid before the auction starts.',
  USER_WITHOUT_PROXY_BID_SUBTEXT: `Your proxy bid must be %s or higher.`,
}

interface BeforeProps {
  featureFlags: FeatureFlags
  performBidVerificationAction: (queueItem: () => void) => void
  user: primitives.AuctionUser
}

const Before = ({
  featureFlags,
  performBidVerificationAction,
  user,
}: BeforeProps) => {
  const { listing, segment } = useListingContext()

  const renderTopBeforeAuction = () => {
    return (
      <div className={cx('u-mb-5')}>
        <TimeInformation segment={segment} />
      </div>
    )
  }

  const renderRight = () => {
    if (!segment) {
      return null
    }

    const isMatchBiddingEnabled =
      segment.configuration?.is_match_bidding_enabled &&
      featureFlags['fe.resi.enable-match-bidding']

    return (
      <>
        {isMatchBiddingEnabled && <MatchBidHeader />}
        <div className={cx('u-mb-6')}>
          <CountdownBefore
            endTime={getStartTimeMoment(segment)}
            currentServerTime={getServerTimeMoment(segment)}
          />
        </div>

        <BidForm
          performBidVerificationAction={performBidVerificationAction}
          featureFlags={featureFlags}
          segment={segment}
          listing={listing}
          user={user}
        >
          {(props: any) => (
            <ProxyBidFormUI
              {...props}
              segment={segment}
              featureFlags={featureFlags}
            />
          )}
        </BidForm>
      </>
    )
  }

  const renderBidDetails = () => {
    return (
      <LeftMarginSection>
        <BidDetails.Before segment={segment} listing={listing} />
      </LeftMarginSection>
    )
  }

  const renderUserWithProxyBid = () => {
    const headerSubText = interpolate(HEADERS.USER_WITH_PROXY_BID_SUBTEXT, [
      formatMoneyWithCentsExceptZero(
        segment?.current_user_max_bid?.max_bid_amount
      ),
    ])
    return (
      <Container
        ribbonColor='success'
        left={
          <>
            {renderTopBeforeAuction()}
            <div className={cx('u-mb-3')}>
              <Header
                color='success'
                icon={<Header.IconSmile />}
                subText={headerSubText}
                text={HEADERS.USER_WITH_PROXY_BID}
              />
            </div>
            {renderBidDetails()}
            <FAQSection />
          </>
        }
        right={renderRight()}
      />
    )
  }

  const renderUserWithoutProxyBid = () => {
    const headerSubText = segment?.starting_bid_amount
      ? interpolate(HEADERS.USER_WITHOUT_PROXY_BID_SUBTEXT, [
          formatMoneyWithCentsExceptZero(segment.starting_bid_amount),
        ])
      : null
    return (
      <Container
        ribbonColor='adcLightBlue'
        left={
          <>
            {renderTopBeforeAuction()}
            <div className={cx('u-mb-3')}>
              <Header
                color='adcLightBlue'
                icon={<Header.IconGavel color='adcLightBlue' />}
                subText={headerSubText}
                text={HEADERS.USER_WITHOUT_PROXY_BID}
              />
            </div>
            {renderBidDetails()}
            <FAQSection />
          </>
        }
        right={renderRight()}
      />
    )
  }

  switch (getBidBannerState(segment, listing)) {
    case BID_BANNER_STATES.USER_WITH_PROXY_BID:
      return renderUserWithProxyBid()
    case BID_BANNER_STATES.USER_WITHOUT_PROXY_BID:
      return renderUserWithoutProxyBid()
    default:
      return null
  }
}

export default Before
